import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Button, Input, SelectPill, Spinner, Icon } from '../../components/ds';
import { useToast } from '../../components/ds';
import api from '../../api/client';
import './ManagePlacementsDrawer.css';

/**
 * ManagePlacementsDrawer
 *
 * Right-side drawer for managing Placements of a Hardcode application.
 * Replaces the old ManagePlacementsModal with a non-shifting slide-over.
 *
 * Three sub-views rendered in-place inside the drawer body:
 *   'list'   — shows all placements with status + edit action
 *   'create' — form to create a new placement
 *   {obj}    — form to edit an existing placement (value is the placement object)
 *
 * Props:
 *   open        {boolean}           Controls drawer visibility
 *   onClose     {Function}          Called on Esc / scrim / close button
 *   accountId   {string}            Account UUID (required by POST /api/admin/placements)
 *   appId       {string}            Application UUID
 *   appName     {string}            Application name (displayed in header)
 *   integration {'hardcode'|'gam'|''} Integration type (for sub header)
 *   archived    {boolean}           Archived gating
 *   onChanged   {Function=}         Called after successful create/update/status change
 */
export function ManagePlacementsDrawer({
  open,
  onClose,
  accountId,
  appId,
  appName,
  integration,
  archived,
  onChanged,
}) {
  const { addToast } = useToast();

  // Sub-view: 'list' | 'create' | placement-object (for edit)
  const [view, setView] = useState('list');

  // Placement list state
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changingStatus, setChangingStatus] = useState(null); // uuid being changed

  // Placement types (fetched once on mount / when drawer opens)
  const [placementTypes, setPlacementTypes] = useState([]);

  // Create / Edit form state
  const [form, setForm] = useState({ placement_name: '', creative_type: '', preview_link: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // ── Reset to list view whenever the drawer opens / app changes ──
  useEffect(() => {
    if (open) {
      setView('list');
      setFormError('');
    }
  }, [open, appId]);

  // ── Reset form when switching views ──
  useEffect(() => {
    if (view === 'create') {
      setForm({ placement_name: '', creative_type: '', preview_link: '' });
      setFormError('');
    } else if (view && view !== 'list') {
      // view is a placement object
      setForm({
        placement_name: view.placement_name || '',
        creative_type: view.creativeType || '',
        preview_link: view.preview_link || '',
      });
      setFormError('');
    }
  }, [view]);

  // ── Fetch placements whenever appId changes or drawer opens ──
  const fetchPlacements = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const data = await api.getApplicationPlacements(appId);
      setPlacements(data.placements || []);
    } catch (err) {
      addToast('載入 Placement 失敗：' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [appId, addToast]);

  useEffect(() => {
    if (open && appId) {
      fetchPlacements();
    }
  }, [open, appId, fetchPlacements]);

  // ── Fetch placement types once ──
  useEffect(() => {
    api.getPlacementTypes()
      .then((types) => setPlacementTypes(types || []))
      .catch(() => {});
  }, []);

  // ── Placement status options (mirrors ManagePlacementsModal logic) ──
  const getPlacementStatusOptions = (status) => {
    switch (status) {
      case 'active':
        return [
          { value: 'active', label: '啟用', dot: 'var(--status-success-border)' },
          { value: 'paused', label: '暫停', dot: 'var(--gray-400)' },
          { value: 'archived', label: '封存', dot: 'var(--gray-300)' },
        ];
      case 'paused':
        return [
          { value: 'paused', label: '已暫停', dot: 'var(--gray-400)' },
          { value: 'active', label: '恢復', dot: 'var(--status-success-border)' },
          { value: 'archived', label: '封存', dot: 'var(--gray-300)' },
        ];
      case 'archived':
        return [
          { value: 'archived', label: '已封存', dot: 'var(--gray-300)' },
          { value: 'paused', label: '取消封存', dot: 'var(--gray-400)' },
        ];
      default:
        return [{ value: status, label: status }];
    }
  };

  // ── Status change handler ──
  const handleChangePlacementStatus = async (uuid, newStatus) => {
    setChangingStatus(uuid);
    try {
      await api.changePlacementStatus(uuid, newStatus);
      const labelMap = { active: '已啟用', paused: '已暫停', archived: '已封存' };
      addToast(`Placement ${labelMap[newStatus] || newStatus}`, 'success');
      fetchPlacements();
      onChanged?.();
    } catch (err) {
      addToast('狀態變更失敗：' + err.message, 'error');
    } finally {
      setChangingStatus(null);
    }
  };

  // ── Form field update helpers ──
  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (formError) setFormError('');
  };

  const setCreativeType = (typeValue) => {
    setForm((prev) => ({ ...prev, creative_type: typeValue }));
    if (formError) setFormError('');
  };

  // ── Create placement ──
  const handleCreate = async () => {
    if (!form.placement_name.trim()) {
      setFormError('請填寫版位名稱');
      return;
    }
    if (!form.creative_type) {
      setFormError('請選擇版位類型');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      await api.createAdminPlacement({
        placement_name: form.placement_name.trim(),
        creative_type: form.creative_type,
        preview_link: form.preview_link.trim(),
        integration_type: 'hardcode',
        application_id: appId,
        account_id: accountId,
      });
      addToast('Placement 已建立', 'success');
      setView('list');
      fetchPlacements();
      onChanged?.();
    } catch (err) {
      addToast('建立 Placement 失敗：' + err.message, 'error');
      setFormError('建立 Placement 失敗：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Update placement ──
  const handleUpdate = async () => {
    if (!form.placement_name.trim()) {
      setFormError('請填寫版位名稱');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      await api.updateAdminPlacement(view.uuid, {
        placement_name: form.placement_name.trim(),
        preview_link: form.preview_link.trim(),
      });
      addToast('Placement 已更新', 'success');
      setView('list');
      fetchPlacements();
      onChanged?.();
    } catch (err) {
      addToast('更新 Placement 失敗：' + err.message, 'error');
      setFormError('更新 Placement 失敗：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived values ──
  const isEditing = view && view !== 'list' && view !== 'create';
  const isFormView = view === 'create' || isEditing;
  const placementCount = placements.length;

  const integrationLabel = integration === 'hardcode' ? 'Hardcode'
    : integration === 'gam' ? 'GAM'
    : '';

  const eyebrow = view === 'list' ? '管理版位'
    : view === 'create' ? '新增版位'
    : '編輯版位';

  const subLine = `${placementCount} placements${integrationLabel ? ' · ' + integrationLabel : ''}`;

  // ── Footer ──
  const footer = !isFormView ? (
    <>
      <Button
        variant="primary"
        size="sm"
        disabled={archived}
        title={archived ? '請先取消封存才能新增 Placement' : undefined}
        onClick={() => !archived && setView('create')}
      >
        <Icon name="plus" size={15} strokeWidth={1.8} />
        新建 Placement
      </Button>
      <span style={{ flex: 1 }} />
      <Button variant="secondary" size="sm" onClick={onClose}>
        關閉
      </Button>
    </>
  ) : (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={submitting}
        onClick={() => setView('list')}
      >
        取消
      </Button>
      <span style={{ flex: 1 }} />
      <Button
        variant="primary"
        size="sm"
        disabled={submitting}
        onClick={view === 'create' ? handleCreate : handleUpdate}
      >
        {submitting
          ? (view === 'create' ? '建立中...' : '儲存中...')
          : (view === 'create' ? '建立' : '儲存')}
      </Button>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={appName}
      sub={subLine}
      footer={footer}
    >
      {/* Archived warning banner */}
      {archived && (
        <div className="mpd-archived-banner">
          <Icon name="lock" size={15} strokeWidth={1.8} />
          <span>此 Application 已封存，請先取消封存才能編輯版位。</span>
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <>
          {loading ? (
            <div className="mpd-loading">
              <Spinner />
            </div>
          ) : placements.length === 0 ? (
            <div className="mpd-empty">
              <Icon name="inbox" size={28} strokeWidth={1.8} color="var(--gray-300)" />
              <div>尚無 Placement，點擊下方「新建 Placement」新增第一個版位。</div>
            </div>
          ) : (
            <div className="mpd-pl-list">
              {placements.map((p) => {
                const ps = p.placement_status;
                const isChanging = changingStatus === p.uuid;
                const isPlacementArchived = ps === 'archived';
                const editDisabled = archived || isPlacementArchived;

                return (
                  <div className="pl-row" key={p.uuid}>
                    {/* Name + creative type */}
                    <div className="pl-row__info">
                      <div className="pl-row__name">{p.placement_name}</div>
                      <div className="pl-row__type">
                        <code>{p.creativeType || '—'}</code>
                      </div>
                    </div>

                    {/* Status SelectPill */}
                    <SelectPill
                      value={ps}
                      options={getPlacementStatusOptions(ps)}
                      disabled={isChanging || archived}
                      headLabel="變更狀態"
                      onChange={(newStatus) => {
                        if (newStatus !== ps) {
                          handleChangePlacementStatus(p.uuid, newStatus);
                        }
                      }}
                    />

                    {/* Edit button */}
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={editDisabled}
                      title={
                        archived
                          ? '請先取消封存 Application 才能編輯'
                          : isPlacementArchived
                          ? '請先取消封存才能編輯'
                          : undefined
                      }
                      onClick={() => {
                        if (!editDisabled) setView(p);
                      }}
                    >
                      編輯
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CREATE / EDIT FORM VIEW ── */}
      {isFormView && (
        <div className="mpd-form">
          {/* Placement name */}
          <Input
            label="版位名稱 *"
            value={form.placement_name}
            onChange={setField('placement_name')}
            placeholder="e.g. 首頁橫幅"
          />

          {/* Creative type */}
          <div className="mpd-field-group">
            <div className="input-label">
              版位類型 (Creative Type){view === 'create' ? ' *' : ''}
            </div>

            {view === 'create' ? (
              <>
                {/* 2-col selectable cards */}
                <div className="mpd-type-grid">
                  {placementTypes.map((pt) => {
                    const typeValue = pt.name;
                    const typeLabel = pt.display_name || pt.name;
                    const selected = form.creative_type === typeValue;
                    return (
                      <button
                        key={typeValue}
                        type="button"
                        className={`mpd-ptype${selected ? ' mpd-ptype--selected' : ''}`}
                        onClick={() => setCreativeType(typeValue)}
                      >
                        <div className="mpd-ptype__name">{typeLabel}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mpd-hint">
                  <Icon name="info" size={12} strokeWidth={1.8} color="var(--text-tertiary)" />
                  建立後版位類型不可修改
                </div>
              </>
            ) : (
              <>
                {/* Locked read-only field */}
                <input
                  type="text"
                  readOnly
                  value={form.creative_type}
                  className="mpd-locked-input"
                  aria-label="版位類型（唯讀）"
                />
                <div className="mpd-hint mpd-hint--lock">
                  <Icon
                    name="lock"
                    size={12}
                    strokeWidth={1.8}
                    color="var(--primary-orange)"
                  />
                  <span>建立後不可修改（PAC 系統限制）</span>
                </div>
              </>
            )}
          </div>

          {/* Preview link */}
          <Input
            label="Preview Link"
            value={form.preview_link}
            onChange={setField('preview_link')}
            placeholder="https://preview.vmfive.com/..."
          />

          {/* Inline form error */}
          {formError && (
            <div className="mpd-form-error">{formError}</div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default ManagePlacementsDrawer;
